// Data layer for the diagnostics timeline. STRICT READ-ONLY — queries only, no
// mutations. Uses getFridgeEventTimeline rather than the shared getFridgeEvents
// wrapper: that one omits `payload` (temps, vend details, swipe status) and takes
// a per-type `limit` instead of a date range.

import API, { graphqlOperation } from '@aws-amplify/api';
import { FridgeHealth } from '../../libs/GraphQL';
import { buildTimeline } from './timeline/buildTimeline';

// Segment/Mixpanel/Checkpoint are excluded downstream by the adapter as noise.
const TIMELINE_EVENT_TYPES = [
  'PurchaseEvent',
  'SwipeEvent',
  'TempEvent',
  'InventoryEvent',
];

// `since`/`until` come back echoed: the range the server ACTUALLY covered, which
// is later than asked when it had to clamp. Selected, not decoration — the caption
// and the gap notice both read them. Both are epoch seconds, like the inputs.
const GET_FRIDGE_EVENT_TIMELINE = `
  query GetFridgeEventTimeline($filter: FilterFridgeEventTimeline!) {
    getFridgeEventTimeline(filter: $filter) {
      since
      until
      truncated
      events {
        location
        eventType
        timestamp
        payload
      }
    }
  }
`;

// getDriverActionTimeline key-ranges the base table on `{locationId}#{_lastModified}`,
// so one location-scoped read returns every requested type. DropOverride is not one
// of them: the backend answers it with a filter on originalDropOffDate, not through
// this key pattern, so adding it here would silently return nothing.
const DRIVER_EVENT_TYPES = [
  'PackingSlipQuery',
  'LocationStocked',
  'DriverFix',
  'LocationUnreachable',
  'ClearOutDecommission',
];

// `eventType` must be in the selection set. One call mixes all five types, so the
// row is the only thing that knows which it is — without it every action would be
// labelled whatever we guessed.
const GET_DRIVER_ACTION_TIMELINE = `
  query GetDriverActionTimeline($filter: FilterDriverActionTimeline!) {
    getDriverActionTimeline(filter: $filter) {
      since
      until
      truncated
      actions { eventType locationId _lastModified data }
    }
  }
`;

// The server names WHY a timeline call failed. Worth translating: "no time zone on
// the location" is a record somebody can go fix, and none of these mean the fridge
// simply had nothing happen — an unexplained empty timeline would read as exactly
// that. Anything unmapped keeps the server's own message.
const TIMELINE_ERROR_MESSAGES = {
  FRIDGE_EVENT_TIMELINE_UNKNOWN_TIMEZONE:
    "This fridge's location record has no time zone, so the server can't place its events on a clock. Set the time zone on the location, then reload — this is a data problem, not an empty timeline.",
  FRIDGE_EVENT_TIMELINE_INVALID_RANGE:
    'The server rejected the requested range (it expects epoch seconds). No events were read.',
  DRIVER_ACTION_TIMELINE_INVALID_RANGE:
    'The server rejected the requested range (it expects epoch seconds). No driver actions were read.',
  FRIDGE_EVENT_TIMELINE_INVERTED_RANGE:
    'The requested range starts at or after it ends. No events were read.',
  DRIVER_ACTION_TIMELINE_INVERTED_RANGE:
    'The requested range starts at or after it ends. No driver actions were read.',
  FRIDGE_EVENT_TIMELINE_UNREADABLE_KEY:
    'The server could not read a timestamp on any row of a page, so it could not say where to continue. Part of the range is missing.',
  FRIDGE_EVENT_TIMELINE_INVALID_FILTER:
    'The server rejected the event query itself. No events were read.',
  DRIVER_ACTION_TIMELINE_INVALID_FILTER:
    'The server rejected the driver-action query itself. No driver actions were read.',
  FRIDGE_EVENT_TIMELINE_QUERY_FAILED:
    'The event read failed. This is a failed read, not an empty timeline — what is shown may be missing rows.',
  DRIVER_ACTION_TIMELINE_QUERY_FAILED:
    'The driver-action read failed. This is a failed read, not an empty timeline — what is shown may be missing rows.',
};

// Amplify surfaces GraphQL errors as `{ errors: [...] }` on a thrown object, and a
// resolver error on an otherwise-2xx response as `errors` on the payload.
function firstGraphQLError(e) {
  return (e && e.errors && e.errors[0]) || e || {};
}

/**
 * Turn whatever a failed call handed us into something worth showing.
 * @returns {{errorType: ?string, message: string}} `errorType` stays null when the
 *   failure never named one (a network drop), which is a different fact from a
 *   server that rejected the request.
 */
function describeTimelineError(e, fallback) {
  const first = firstGraphQLError(e);
  const errorType = first.errorType || null;
  return {
    errorType,
    message:
      (errorType && TIMELINE_ERROR_MESSAGES[errorType]) ||
      first.message ||
      fallback,
  };
}

// fridgeData is an object or a single-element array depending on the resolver.
function extractFridgeData(fridgeHealthResponse, location) {
  const list = fridgeHealthResponse?.data?.fridgeHealth || [];
  for (const fh of list) {
    const fd = fh?.fridgeData;
    if (Array.isArray(fd) && fd.length)
      return fd.find((x) => x?.locationId === location) || fd[0];
    if (fd && (fd.locationId || fd.temperature !== undefined)) return fd;
  }
  return null;
}

function parseData(v) {
  if (v == null) return {};
  if (typeof v === 'object') return v;
  try {
    return JSON.parse(v);
  } catch (e) {
    return {};
  }
}

// Both halves take epoch seconds and the server resolves the fridge's own time zone,
// so there is no local wall clock to build and no location record to fetch first.
// A range is [since, until): `since` is included, `until` is not.
function todaySoFar() {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return {
    startSec: Math.floor(start.getTime() / 1000),
    endSec: Math.floor(Date.now() / 1000),
  };
}

// `since == until` is now rejected outright rather than answered with nothing, and
// the empty range a picker can produce at midnight is worth a second of data, not an
// error banner. One second is the smallest range the contract allows.
function normalizeWindow(w) {
  const startSec = Math.floor(w.startSec);
  return { startSec, endSec: Math.max(Math.floor(w.endSec), startSec + 1) };
}

// Exported so the UI can name the limit it's reporting rather than restating "7".
// The server clamps a single request to 7 days and says `truncated`; we page back
// from there, so this is the CLIENT's choice of how far back to look, not a hard
// ceiling. The echoed `since` of each half, not this constant, is what says how much
// was actually covered.
export const MAX_WINDOW_DAYS = 7;

// Each page is a round trip and they cannot run concurrently, so a dense fridge over
// a long range has to stop somewhere. Reaching this is reported as a gap, never as a
// complete timeline. Exported so the gap copy can name it.
export const MAX_TIMELINE_PAGES = 12;

/**
 * Walk a timeline query's pages. Both queries page identically: ask for
 * `[since, until)`, and while the answer is `truncated`, ask again with the SAME
 * `since` and `until` set to the `since` that came back. Pages don't overlap, so
 * rows are concatenated without deduping.
 *
 * @param {(sinceSec: number, untilSec: number) => Promise<Object>} fetchPage Resolves
 *   `{rows, since, until, truncated}`; throws on a failed read.
 * @param {number} startSec Inclusive start, epoch seconds. Same on every page.
 * @param {number} endSec Exclusive end, epoch seconds, for the FIRST page only.
 * @returns {Promise<{rows: Object[], since: ?number, until: ?number,
 *   truncated: ?boolean, error: ?Object, pages: number}>} `since`/`until` are the
 *   range actually covered across every page. `truncated` is false only when a page
 *   said so — a server that never reported leaves it null, because unknown coverage
 *   is not complete coverage.
 */
async function fetchTimelinePages(fetchPage, startSec, endSec) {
  const rows = [];
  let until = endSec;
  let coveredUntil = null;
  let coveredSince = null;
  let pages = 0;

  const result = (truncated, error) => ({
    rows,
    since: coveredSince,
    until: coveredUntil,
    truncated,
    error: error || null,
    pages,
  });

  while (pages < MAX_TIMELINE_PAGES) {
    let page;
    try {
      // Each request's `until` is the previous response's `since`, so the pages can
      // only be walked one at a time.
      // eslint-disable-next-line no-await-in-loop
      page = await fetchPage(startSec, until);
    } catch (e) {
      // Whatever earlier pages returned is still real. We only ever ask for another
      // page because the last one said `truncated`, so past the first page the range
      // is known short; on the first there is nothing to judge from.
      return result(pages > 0 ? true : null, e);
    }
    rows.push(...page.rows);
    if (pages === 0) coveredUntil = page.until;
    if (page.since != null) coveredSince = page.since;
    pages += 1;

    // false = the range is complete. null = the server didn't say, and paging on
    // would be guessing.
    if (page.truncated !== true) return result(page.truncated ?? null, null);

    // A truncated page whose `since` didn't move back gives no smaller `until` to
    // ask for; one extremely dense second would otherwise repeat forever.
    if (page.since == null || page.since >= until) return result(true, null);
    until = page.since;
  }

  return result(true, null);
}

/**
 * Fetch the events in a window, plus the server's report of what it covered.
 * A malformed row arrives as `null` in the list instead of failing the query, so
 * nulls are dropped here rather than in every adapter downstream.
 */
async function fetchFridgeEvents(location, startSec, endSec) {
  return fetchTimelinePages(
    async (sinceSec, untilSec) => {
      const res = await API.graphql(
        graphqlOperation(GET_FRIDGE_EVENT_TIMELINE, {
          filter: {
            location,
            eventTypes: TIMELINE_EVENT_TYPES,
            since: sinceSec,
            until: untilSec,
          },
        }),
      );
      if (res.errors?.length) throw res;
      const t = res.data?.getFridgeEventTimeline;
      return {
        rows: (t?.events || []).filter(Boolean),
        since: t?.since ?? null,
        until: t?.until ?? null,
        truncated: t?.truncated ?? null,
      };
    },
    startSec,
    endSec,
  );
}

/**
 * Fetch this fridge's driver actions in a window, plus what the server covered.
 * Nulls are dropped for the same reason as in the event half.
 */
async function fetchDriverActions(location, startSec, endSec) {
  const page = await fetchTimelinePages(
    async (sinceSec, untilSec) => {
      const res = await API.graphql(
        graphqlOperation(GET_DRIVER_ACTION_TIMELINE, {
          filter: {
            location,
            eventTypes: DRIVER_EVENT_TYPES,
            since: sinceSec,
            until: untilSec,
          },
        }),
      );
      if (res.errors?.length) throw res;
      const t = res.data?.getDriverActionTimeline;
      return {
        rows: (t?.actions || []).filter(Boolean),
        since: t?.since ?? null,
        until: t?.until ?? null,
        truncated: t?.truncated ?? null,
      };
    },
    startSec,
    endSec,
  );

  // Already newest-first within each page, and pages walk backwards, so the
  // concatenation stays newest-first; re-sorting here would only mask it if that
  // ever stopped holding.
  return {
    ...page,
    actions: page.rows.map((a) => ({
      actionType: a.eventType,
      timestamp: a._lastModified,
      data: parseData(a.data),
      locationId: a.locationId,
    })),
  };
}

/**
 * Assemble the live timeline for `window`, or for today so far when there is none.
 * @returns {Promise<Object>} `resolvedWindow` is the range actually requested (the
 *   default is concrete, not implied); `timeline` is `{beats, series}`; `coverage`
 *   is what each half of the server said it covered; `gaps` is every way the result
 *   fell short, from either.
 */
export async function loadLiveTimeline(location, { window = null } = {}) {
  const resolvedWindow = normalizeWindow(window || todaySoFar());
  const [eventsResult, healthResp, driver] = await Promise.all([
    fetchFridgeEvents(location, resolvedWindow.startSec, resolvedWindow.endSec),
    FridgeHealth({ locationIds: [location] }),
    fetchDriverActions(
      location,
      resolvedWindow.startSec,
      resolvedWindow.endSec,
    ),
  ]);

  // A first-page event failure means there is no timeline to draw at all, so it is
  // the page's error rather than a footnote under rows that aren't there. A later
  // page failing leaves real rows on screen and becomes a gap below.
  if (eventsResult.error && eventsResult.pages === 0) {
    const { errorType, message } = describeTimelineError(
      eventsResult.error,
      'Failed to load fridge events.',
    );
    const err = new Error(message);
    err.errorType = errorType;
    throw err;
  }

  const fridgeData = extractFridgeData(healthResp, location);
  const timeline = buildTimeline({
    fridgeEvents: eventsResult.rows,
    healthSnapshot: fridgeData,
    driverActions: driver.actions,
  });

  // One channel for every kind of incompleteness. Each half reports its own stop,
  // so nothing here re-derives "how much did we cover" from the range we asked for.
  const gaps = [];
  if (driver.error) {
    const { errorType, message } = describeTimelineError(
      driver.error,
      'Driver actions could not be loaded.',
    );
    console.error('getDriverActionTimeline failed', driver.error);
    gaps.push({ reason: 'driver-failed', errorType, message });
  }
  if (eventsResult.error) {
    const { errorType, message } = describeTimelineError(
      eventsResult.error,
      'Some events could not be loaded.',
    );
    console.error('getFridgeEventTimeline failed', eventsResult.error);
    gaps.push({ reason: 'events-failed', errorType, message });
  }
  if (driver.truncated === true)
    gaps.push({
      reason: 'driver-truncated',
      since: driver.since,
      atPageCap: driver.pages >= MAX_TIMELINE_PAGES,
    });
  if (eventsResult.truncated === true)
    gaps.push({
      reason: 'events-truncated',
      since: eventsResult.since,
      atPageCap: eventsResult.pages >= MAX_TIMELINE_PAGES,
    });

  return {
    fridgeData,
    timeline,
    eventCount: eventsResult.rows.length,
    beatCount: timeline.beats.length,
    driverCount: driver.actions.length,
    gaps,
    resolvedWindow,
    coverage: {
      events: {
        since: eventsResult.since,
        until: eventsResult.until,
        truncated: eventsResult.truncated,
      },
      driver: {
        since: driver.since,
        until: driver.until,
        truncated: driver.truncated,
      },
    },
  };
}
